import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, Database, Bot, Layers, Icon } from "lucide-react"
import { api } from '@/lib/api'
import { seo } from '@shared/seo.config'
import { Seo } from '@/components/Seo'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const pillars = [
  {
    icon: Layers,
    title: 'Một mã nguồn',
    body: 'Client trong src/, server trong server/. Type của database chảy thẳng ra component — đổi một cột là TypeScript báo lỗi ngay tại chỗ dùng.',
  },
  {
    icon: Database,
    title: 'Database của bạn',
    body: 'Đổi đúng một dòng DATABASE_URL là đổi cả hệ thống lưu trữ. Postgres nào cũng chạy: VPS tự dựng, Neon, RDS. Không khoá vào nhà cung cấp nào.',
  },
  {
    icon: Bot,
    title: 'AI đọc được',
    body: 'robots.txt, sitemap.xml và llms.txt sinh tự động. Worker trả HTML có nội dung cho crawler của ChatGPT, Claude, Perplexity — thứ SPA thường vô hình với họ.',
  },
]

export default function Home() {
  const health = useQuery({ queryKey: ['health'], queryFn: api.health })
  const connected = health.data?.database === 'configured'

  return (
    <>
      <Seo />

      {/* Hero */}
      <section data-upbo-src="src/pages/Home.tsx:38" className="relative overflow-hidden border-b border-border/60">
        {/* Lưới nền rất nhạt — tạo chiều sâu mà không tranh chỗ với chữ. */}
        <div data-upbo-src="src/pages/Home.tsx:40"
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.035] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]"
        />
        <div data-upbo-src="src/pages/Home.tsx:44" className="relative mx-auto w-full max-w-6xl px-6 pt-24 pb-20">
          <StatusPill loading={health.isLoading} connected={connected} />

          <h1 data-upbo-src="src/pages/Home.tsx:47" className="mt-8 max-w-3xl text-balance text-5xl font-semibold leading-[1.05] tracking-[-0.03em] sm:text-6xl">
            {seo.siteName}
          </h1>
          <p data-upbo-src="src/pages/Home.tsx:50" className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            {seo.description}
          </p>

          <div data-upbo-src="src/pages/Home.tsx:54" className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-xs text-muted-foreground">
            <span data-upbo-src="src/pages/Home.tsx:55">src/ — giao diện</span>
            <span data-upbo-src="src/pages/Home.tsx:56" className="text-border">/</span>
            <span data-upbo-src="src/pages/Home.tsx:57">server/ — API + database</span>
            <span data-upbo-src="src/pages/Home.tsx:58" className="text-border">/</span>
            <span data-upbo-src="src/pages/Home.tsx:59">shared/ — dùng chung</span>
          </div>
        </div>
      </section>

      {/* Ba trụ cột */}
      <section data-upbo-src="src/pages/Home.tsx:65" className="mx-auto w-full max-w-6xl px-6 py-20">
        <div data-upbo-src="src/pages/Home.tsx:66" className="grid gap-px overflow-hidden rounded-xl border bg-border sm:grid-cols-3">
          {pillars.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="rounded-none border-0 bg-background shadow-none">
              <CardContent className="flex flex-col gap-3 py-2">
                <Icon className="size-5 text-muted-foreground" strokeWidth={1.5} />
                <h2 data-upbo-src="src/pages/Home.tsx:71" className="font-medium tracking-tight">{title}</h2>
                <p data-upbo-src="src/pages/Home.tsx:72" className="text-sm leading-relaxed text-muted-foreground">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <a data-upbo-src="src/pages/Home.tsx:78"
          href="/llms.txt"
          className="group mt-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          Xem llms.txt mà AI đọc được
          <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </section>
    </>
  )
}

/** Trạng thái database — thứ đầu tiên người dùng cần biết khi mở dự án lần đầu. */
function StatusPill({ loading, connected }: { loading: boolean; connected: boolean }) {
  if (loading) return <Skeleton className="h-6 w-44" />
  return (
    <Badge variant="outline" className="gap-2 px-3 py-1 font-normal">
      <span data-upbo-src="src/pages/Home.tsx:95"
        className={cn(
          'size-1.5 rounded-full',
          connected ? 'bg-emerald-500' : 'bg-amber-500',
        )}
      />
      {connected ? 'Đã kết nối database' : 'Chưa đặt DATABASE_URL'}
    </Badge>
  )
}
